<template>
  <section class="omx-list">

    <!-- Header: title + description + action slot -->
    <header class="omx-list__head" v-if="title || description || $slots.actions || $slots['head-extra']">
      <div class="omx-list__head-text" v-if="title || description">
        <h2 v-if="title" class="omx-list__title">{{ title }}</h2>
        <p v-if="description" class="omx-list__desc">{{ description }}</p>
      </div>
      <div class="omx-list__head-actions">
        <slot name="head-extra" />
        <slot name="actions" />
      </div>
    </header>

    <!-- Filter rail: search input + segmented + chips -->
    <div class="omx-list__filters" v-if="hasFilters">
      <div class="omx-list__search" v-if="showSearch">
        <b-icon icon="magnify" size="is-small" />
        <input
            type="text"
            :value="searchValue"
            :placeholder="searchPlaceholder || $t('omx-list.search-placeholder')"
            @input="$emit('update:searchValue', $event.target.value)"
        />
        <button v-if="searchValue" class="omx-list__search-clear" @click="$emit('update:searchValue', '')" aria-label="Clear">×</button>
      </div>

      <div class="omx-list__seg" v-if="segments && segments.length">
        <button
            v-for="seg in segments"
            :key="seg.value"
            :class="{ 'is-on': seg.value === segmentValue }"
            @click="$emit('update:segmentValue', seg.value)"
        >{{ seg.label }}<span v-if="seg.count != null" class="omx-list__seg-count">{{ seg.count }}</span></button>
      </div>

      <slot name="chips" />
    </div>

    <!-- Bulk-action toolbar (visible only when selection has items) -->
    <div class="omx-list__bulkbar" v-if="selectable && selected.length">
      <span class="omx-list__bulkbar-count">{{ $tc('omx-list.selected-count', selected.length, { n: selected.length }) }}</span>
      <slot name="bulk-actions" :selected="selected" />
      <button class="omx-list__bulkbar-clear" @click="$emit('update:selected', [])">{{ $t('omx-list.clear-selection') }}</button>
    </div>

    <!-- Body: skeleton / empty / table -->
    <div class="omx-list__body">
      <div v-if="loading && !data.length" class="omx-list__skel">
        <OmxSkeleton
            v-for="i in skeletonRows"
            :key="'skel-' + i"
            variant="table-row"
            :columns="skeletonColumns"
        />
      </div>

      <OmxEmpty
          v-else-if="!data.length"
          :icon="emptyIcon"
          :title="emptyTitle || $t('omx-list.empty-title')"
          :body="emptyBody"
          tone="brand"
      >
        <slot name="empty-actions" />
      </OmxEmpty>

      <b-table
          v-else
          class="omx-list__table"
          :data="data"
          :striped="false"
          :hoverable="true"
          :mobile-cards="true"
          :scrollable="true"
          :loading="loading"
          :checkable="selectable"
          :checked-rows="selected"
          @check="rows => $emit('update:selected', rows)"
          :paginated="paginated"
          :per-page="perPage"
          :pagination-simple="false"
          :pagination-position="'bottom'"
          :default-sort="defaultSort"
      >
        <slot />
      </b-table>
    </div>

    <!-- Footer slot for extra meta -->
    <footer class="omx-list__foot" v-if="$slots.foot">
      <slot name="foot" />
    </footer>
  </section>
</template>

<script>
import OmxSkeleton from "./OmxSkeleton.vue";
import OmxEmpty from "./OmxEmpty.vue";

/**
 * OmxList — uniform CRUD list surface.
 *
 * Replaces the duplicated header + b-table + (no) empty + (no) skeleton
 * pattern across Projects, Tasks, Personnel, Consumables, Equipment.
 *
 * Keeps b-table under the hood so existing <b-table-column> usage works.
 *
 *   <OmxList
 *       :title="$t('projects-view.title')"
 *       :data="projectsData"
 *       :loading="isLoading"
 *       :search-value.sync="search"
 *       :segment-value.sync="activePhase"
 *       :segments="phaseSegments"
 *       :selectable="true"
 *       :selected.sync="selectedRows"
 *       empty-icon="folder-open-outline"
 *       :empty-title="$t('projects-view.empty-title')"
 *       :empty-body="$t('projects-view.empty-body')"
 *   >
 *     <template #actions> <b-button …/> </template>
 *     <template #chips>   <span class="chip">…</span> </template>
 *     <b-table-column field="project.code" …> … </b-table-column>
 *     …
 *     <template #empty-actions> <b-button>＋ Create</b-button> </template>
 *     <template #bulk-actions="{selected}"> <b-button @click="archive(selected)">Archive</b-button> </template>
 *   </OmxList>
 */
export default {
  name: "OmxList",
  components: { OmxSkeleton, OmxEmpty },
  props: {
    title:          { type: String, default: null },
    description:    { type: String, default: null },
    data:           { type: Array, required: true },
    loading:        { type: Boolean, default: false },

    showSearch:     { type: Boolean, default: true },
    searchValue:    { type: String, default: "" },
    searchPlaceholder: { type: String, default: null },

    segments:       { type: Array, default: null }, // [{ value, label, count? }]
    segmentValue:   { type: [String, Number, Boolean], default: null },

    selectable:     { type: Boolean, default: false },
    selected:       { type: Array, default: () => [] },

    paginated:      { type: Boolean, default: false },
    perPage:        { type: Number, default: 20 },
    defaultSort:    { type: Array, default: null },

    emptyIcon:      { type: String, default: "tray-arrow-down" },
    emptyTitle:     { type: String, default: null },
    emptyBody:      { type: String, default: null },

    skeletonRows:    { type: Number, default: 6 },
    skeletonColumns: { type: Array, default: () => [1.2, 1.6, 1.4, 1, 1, 1.4] }
  },
  computed: {
    hasFilters() {
      return this.showSearch || (this.segments && this.segments.length) || !!this.$slots.chips;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ---- Header ------------------------------------------------- */
.omx-list__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.omx-list__head-text { min-width: 0; }
.omx-list__title {
  margin: 0 0 2px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.012em;
  color: $omx-ink-900;
}
.omx-list__desc {
  margin: 0;
  color: $omx-ink-500;
  font-size: 13px;
}
.omx-list__head-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* ---- Filter rail ------------------------------------------- */
.omx-list__filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.omx-list__search {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid $omx-ink-200;
  background: #fff;
  border-radius: 8px;
  padding: 6px 12px;
  min-width: 220px;
  flex: 1;
  max-width: 420px;
  color: $omx-ink-500;
  input {
    border: none;
    outline: none;
    flex: 1;
    background: transparent;
    color: $omx-ink-900;
    font-size: 13.5px;
  }
}
.omx-list__search-clear {
  border: none;
  background: transparent;
  color: $omx-ink-400;
  font-size: 18px;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  &:hover { color: $omx-ink-700; }
}
.omx-list__seg {
  display: inline-flex;
  border: 1px solid $omx-ink-200;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  button {
    border: none;
    background: transparent;
    padding: 6px 12px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    color: $omx-ink-500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    &:hover { color: $omx-brand-700; }
    &.is-on {
      background: $omx-brand-600;
      color: #fff;
    }
  }
}
.omx-list__seg-count {
  background: rgba(0,0,0,.08);
  color: inherit;
  font-size: 11px;
  border-radius: 999px;
  padding: 0 6px;
  font-weight: 700;
  .is-on & { background: rgba(255,255,255,.25); color: #fff; }
}

/* ---- Bulk bar --------------------------------------------- */
.omx-list__bulkbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: $omx-brand-50;
  border: 1px solid $omx-brand-100;
  border-radius: $omx-radius-md;
  color: $omx-brand-700;
  font-size: 13px;
}
.omx-list__bulkbar-count { font-weight: 600; }
.omx-list__bulkbar-clear {
  background: transparent;
  border: none;
  color: $omx-brand-700;
  cursor: pointer;
  margin-left: auto;
  text-decoration: underline;
  font-size: 12.5px;
}

/* ---- Body ------------------------------------------------- */
.omx-list__body {
  background: #fff;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-lg;
  overflow: hidden;
}
.omx-list__skel { padding: 4px 0; }

.omx-list__foot {
  font-size: 12px;
  color: $omx-ink-500;
  padding: 4px 4px 0;
}

/* Tighten Buefy's default table chrome inside OmxList */
::v-deep .omx-list__table {
  .table {
    background: transparent;
    margin-bottom: 0;
  }
  th {
    background: #fbfcfe !important;
    border-bottom: 1px solid $omx-ink-100 !important;
    color: $omx-ink-500 !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: .08em;
    text-transform: uppercase;
  }
  td {
    border-bottom: 1px solid $omx-ink-100 !important;
    color: $omx-ink-700;
    font-size: 13.5px;
    vertical-align: middle;
  }
  tr:last-child td { border-bottom: none !important; }
  tr.is-checked td { background: $omx-brand-50 !important; }
  .pagination { padding: 10px 14px; }
}
</style>
